import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Building2, Eye, EyeOff, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import AuthService from "@/services/APIs/AuthService";
import {
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalUserInfo,
  getAccessToken,
  setLocalTenantId,
} from "@/lib/storeManager";
import navigationRoutes from "@/project/propertyPortal/utils/navigationRoutes";

const LOGIN_FAILED_DEFAULT =
  "Invalid email or password. Please check your credentials and try again.";

/** Normalize Django/DRF and common API error payloads into a single string. */
function getLoginErrorMessage(error: unknown): string {
  if (error === undefined || error === null) {
    return LOGIN_FAILED_DEFAULT;
  }

  const err = error as {
    message?: string;
    response?: { data?: unknown; status?: number };
    code?: string;
  };

  if (err.code === "ERR_NETWORK") {
    return "Unable to reach the server. Check your connection and try again.";
  }

  const data = err.response?.data;
  if (data === undefined || data === null) {
    return typeof err.message === "string" && err.message
      ? err.message
      : LOGIN_FAILED_DEFAULT;
  }

  if (typeof data === "string") {
    return data;
  }

  const record = data as Record<string, unknown>;

  if (record.detail !== undefined && record.detail !== null) {
    const d = record.detail;
    if (Array.isArray(d)) {
      return d.map(String).join(" ");
    }
    return String(d);
  }

  if (record.message !== undefined && record.message !== null) {
    return String(record.message);
  }

  if (record.error !== undefined && record.error !== null) {
    const e = record.error;
    if (typeof e === "string") {
      return e;
    }
    if (e && typeof e === "object" && "message" in e) {
      return String((e as { message: unknown }).message);
    }
  }

  if (
    Array.isArray(record.non_field_errors) &&
    record.non_field_errors.length
  ) {
    return record.non_field_errors.map(String).join(" ");
  }

  const fieldErrors = Object.entries(record).filter(
    ([, v]) => Array.isArray(v) && v.length
  );
  if (fieldErrors.length) {
    return fieldErrors
      .flatMap(([, v]) => (v as unknown[]).map(String))
      .join(" ");
  }

  return LOGIN_FAILED_DEFAULT;
}

const PP_Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authErrorOpen, setAuthErrorOpen] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState("");

  // Optional: ?tenant=<uuid> for API headers when tenant is not in storage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tenant");
    if (t) {
      setLocalTenantId(t);
    }
  }, []);

  // Check if already logged in
  useEffect(() => {
    const token = getAccessToken();
    if (token) {
      navigate(navigationRoutes.DASHBOARD, { replace: true });
    }
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Use real authentication API
      const response = await AuthService.login({ email, password });

      if (response) {
        // Store tokens and user info
        setLocalAccessToken(response.access);
        setLocalRefreshToken(response.refresh);
        setLocalUserInfo({
          first_name: response.user?.first_name ?? "",
          last_name: response.user?.last_name ?? "",
          email: response.user?.email ?? email,
          user_type: response.user?.user_type ?? "",
        });

        toast.success("Login successful!");
        navigate(navigationRoutes.DASHBOARD);
      }
    } catch (error: unknown) {
      console.error("Login error:", error);
      setAuthErrorMessage(getLoginErrorMessage(error));
      setAuthErrorOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-primary via-primary/90 to-secondary p-4">
      <AlertDialog open={authErrorOpen} onOpenChange={setAuthErrorOpen}>
        <AlertDialogContent className="border-border sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Sign-in failed</AlertDialogTitle>
            <AlertDialogDescription className="text-left text-base text-foreground">
              {authErrorMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              className="w-full bg-gradient-to-r from-primary to-secondary text-primary-foreground sm:w-auto"
              onClick={() => setAuthErrorOpen(false)}
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="space-y-4 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-secondary">
            <Building2 className="h-10 w-10 text-primary-foreground" />
          </div>
          <div>
            <CardTitle className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-3xl font-bold text-transparent">
              Property Investment
            </CardTitle>
            <CardDescription className="mt-2 text-base">
              Sign in to manage your property portfolio
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="h-12 pl-10"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Password
              </Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="h-12 pl-10 pr-11"
                  required
                />
                <button
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  onClick={() => setShowPassword(v => !v)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="h-12 w-full bg-gradient-to-r from-primary to-secondary text-base font-semibold transition-opacity hover:opacity-90"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PP_Login;
